/**
 * Files Loader - Loads files and summaries with caching
 * 
 * Handles file loading with IDB and localStorage caching
 */

/**
 * FilesLoader Class - Loads files and summaries
 */
class FilesLoader {
    constructor(localStorageManager, filterMetadataGenerator) {
        this.localStorageManager = localStorageManager;
        this.filterMetadataGenerator = filterMetadataGenerator;
        this.currentVersion = '4.0.0';
    }
    
    /**
     * Check if cache is valid based on server timestamp
     * @param {string} url - Files API URL
     * @param {Function} lastModified - Function to get last modified date
     * @param {Object} idbModule - IDB module with functions
     * @returns {Promise<boolean>} - Cache validity status
     */
    async isCacheValid(url = '/api/files/', lastModified, idbModule) {
        try {
            // Prefer IDB meta if available
            if (idbModule.isIdbSupported() && idbModule.isIdbEnabled()) {
                const db = await idbModule.openDatabase();
                try {
                    const meta = await idbModule.idbGetMeta(db);
                    if (!meta || !meta.lastModified) {
                        // No cache yet - need to fetch
                        return false;
                    }
                    // Pass cached date to lastModified() so it sends If-Modified-Since header
                    const serverDate = lastModified(url, meta.lastModified);
                    // If server returned 304, cache is valid (serverDate will be same as cached date)
                    // If we can't check server date (HEAD request failed), assume cache is valid
                    // to avoid unnecessary cache-busting requests
                    if (serverDate === null || isNaN(serverDate)) {
                        // Server date unavailable - use cached data if we have it
                        return true;
                    }
                    // Cache is valid if server date is not newer than cached date
                    // If serverDate equals cached date (304 response), cache is valid
                    const valid = serverDate <= (meta.lastModified || 0);
                    return valid;
                } finally { idbModule.closeDatabase(db); }
            }
        } catch (_) { /* fallback to localStorage below */ }

        // Fallback: localStorage timestamp only
        try {
            const cachedDate = this.localStorageManager.getCachedDate();
            if (!cachedDate) return false;
            // Pass cached date to lastModified() so it sends If-Modified-Since header
            const serverDate = lastModified(url, cachedDate);
            // If we can't check server date, assume cache is valid to avoid unnecessary requests
            if (serverDate === null || isNaN(serverDate)) {
                return true;
            }
            return serverDate <= cachedDate;
        } catch (_) { 
            // On error, assume cache is invalid (safer to fetch)
            return false; 
        }
    }
    
    /**
     * Load files with intelligent caching
     * @param {Object} options - Loading options
     * @param {Function} lastModified - Function to get last modified date
     * @param {Object} idbModule - IDB module with functions
     * @param {Object} fileLoader - File loader module
     * @param {Object} fileProcessor - File processor module
     * @returns {Promise<Object>} - Result with files and metadata
     */
    async loadFiles(options = {}, lastModified, idbModule, fileLoader, fileProcessor) {
        const {
            url = '/api/files/',
            forceUpdate = false,
            processFiles = true,
            processingOptions = {},
            onProgress = null,
            skipCacheCheck = false  // If true, skip cache check (already done externally)
        } = options;
        
        const startTime = Date.now();
        
        // Check cache first (unless forced update or cache check was already done externally)
        // Note: If isCacheValid() was already called, skipCacheCheck=true to avoid duplicate HEAD request
        if (!forceUpdate) {
            try {
                if (idbModule.isIdbSupported() && idbModule.isIdbEnabled()) {
                    const db = await idbModule.openDatabase();
                    try {
                        const meta = await idbModule.idbGetMeta(db);
                        // Only check server if we have cache and haven't already validated it
                        if (meta && meta.lastModified) {
                            if (skipCacheCheck) {
                                // Cache was already validated externally (via isCacheValid()), use it directly
                                const tRead = (typeof performance !== 'undefined' ? performance.now() : Date.now());
                                const records = await idbModule.idbGetAll(db);
                                const readMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - tRead;
                                
                                // DEBUG: Log UTF-8 encoding check after reading from IndexedDB
                                if (Array.isArray(records) && records.length > 0) {
                                    const testFile = records.find(f => (f.n || f.name || '').toLowerCase().includes('zus') && (f.n || f.name || '').toLowerCase().includes('tzliche'));
                                    if (testFile) {
                                        const fileName = testFile.n || testFile.name || '';
                                        console.log('[FilesLoader] UTF-8 DEBUG - After idbGetAll (skipCacheCheck):', {
                                            fileName: fileName,
                                            includesA: fileName.includes('Ä'),
                                            includesAumlaut: fileName.includes('\u00C4'),
                                            charCodes: Array.from(fileName).slice(0, 20).map(c => c.charCodeAt(0)),
                                            bytes: Array.from(new TextEncoder().encode(fileName)).slice(0, 20)
                                        });
                                    }
                                }
                                
                                const processedFiles = processFiles
                                    ? fileProcessor.processFilesList(records, processingOptions)
                                    : records;
                                
                                // DEBUG: Log UTF-8 encoding check after processing
                                if (Array.isArray(processedFiles) && processedFiles.length > 0) {
                                    const testFile = processedFiles.find(f => (f.n || f.name || '').toLowerCase().includes('zus') && (f.n || f.name || '').toLowerCase().includes('tzliche'));
                                    if (testFile) {
                                        const fileName = testFile.n || testFile.name || '';
                                        console.log('[FilesLoader] UTF-8 DEBUG - After processing (skipCacheCheck):', {
                                            fileName: fileName,
                                            includesA: fileName.includes('Ä'),
                                            includesAumlaut: fileName.includes('\u00C4')
                                        });
                                    }
                                }
                                // Ensure date_min/date_max in meta; compute if missing
                                try {
                                    if (!meta.date_min || !meta.date_max) {
                                        const bounds = this.filterMetadataGenerator.computeDateBounds(processedFiles);
                                        await idbModule.idbSetMeta(db, { ...meta, date_min: bounds.min || null, date_max: bounds.max || null });
                                    }
                                } catch(_){ }
                                const loadTime = Date.now() - startTime;
                                
                                if(window.ComponentLoadingStatus){
                                    let caption = 'Files loaded from IndexedDB';
                                    if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                                      const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                                      caption = window.TranslationRegistry.translate('loader_stage_files_loaded_from_indexeddb', lang);
                                    }
                                    window.ComponentLoadingStatus.update('filesStore', 'ready', caption);
                                }
                                
                                return { 
                                    files: processedFiles, 
                                    source: 'idb', 
                                    timestamp: meta.lastModified || null, 
                                    loadTime, 
                                    stats: this.localStorageManager.getStats(),
                                    hasOtAccess: !!meta.hasOtAccess 
                                };
                            }
                            
                            // Pass cached date to lastModified() so it sends If-Modified-Since header
                            // This will return 304 if data hasn't changed, avoiding GET request
                            const serverDate = lastModified(url, meta.lastModified);
                            // If server returned 304 (serverDate equals cached date), cache is valid
                            // If we can't check server date (HEAD request failed), assume cache is valid
                            // to avoid unnecessary cache-busting requests
                            if (serverDate === null || isNaN(serverDate)) {
                                // Server date unavailable - use cached data if we have it
                                const tRead = (typeof performance !== 'undefined' ? performance.now() : Date.now());
                                const records = await idbModule.idbGetAll(db);
                                const readMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - tRead;
                                
                                // DEBUG: Log UTF-8 encoding check after reading from IndexedDB
                                if (Array.isArray(records) && records.length > 0) {
                                    const testFile = records.find(f => (f.n || f.name || '').toLowerCase().includes('zus') && (f.n || f.name || '').toLowerCase().includes('tzliche'));
                                    if (testFile) {
                                        const fileName = testFile.n || testFile.name || '';
                                        console.log('[FilesLoader] UTF-8 DEBUG - After idbGetAll (serverDate null):', {
                                            fileName: fileName,
                                            includesA: fileName.includes('Ä'),
                                            includesAumlaut: fileName.includes('\u00C4'),
                                            charCodes: Array.from(fileName).slice(0, 20).map(c => c.charCodeAt(0)),
                                            bytes: Array.from(new TextEncoder().encode(fileName)).slice(0, 20)
                                        });
                                    }
                                }
                                
                                const processedFiles = processFiles
                                    ? fileProcessor.processFilesList(records, processingOptions)
                                    : records;
                                
                                // DEBUG: Log UTF-8 encoding check after processing
                                if (Array.isArray(processedFiles) && processedFiles.length > 0) {
                                    const testFile = processedFiles.find(f => (f.n || f.name || '').toLowerCase().includes('zus') && (f.n || f.name || '').toLowerCase().includes('tzliche'));
                                    if (testFile) {
                                        const fileName = testFile.n || testFile.name || '';
                                        console.log('[FilesLoader] UTF-8 DEBUG - After processing (serverDate null):', {
                                            fileName: fileName,
                                            includesA: fileName.includes('Ä'),
                                            includesAumlaut: fileName.includes('\u00C4')
                                        });
                                    }
                                }
                                // Ensure date_min/date_max in meta; compute if missing
                                try {
                                    if (!meta.date_min || !meta.date_max) {
                                        const bounds = this.filterMetadataGenerator.computeDateBounds(processedFiles);
                                        await idbModule.idbSetMeta(db, { ...meta, date_min: bounds.min || null, date_max: bounds.max || null });
                                    }
                                } catch(_){ }
                                const loadTime = Date.now() - startTime;
                                
                                if(window.ComponentLoadingStatus){
                                    let caption = 'Files loaded from IndexedDB';
                                    if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                                      const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                                      caption = window.TranslationRegistry.translate('loader_stage_files_loaded_from_indexeddb', lang);
                                    }
                                    window.ComponentLoadingStatus.update('filesStore', 'ready', caption);
                                }
                                
                                return { 
                                    files: processedFiles, 
                                    source: 'idb', 
                                    timestamp: meta.lastModified || null, 
                                    loadTime, 
                                    stats: this.localStorageManager.getStats(),
                                    hasOtAccess: !!meta.hasOtAccess 
                                };
                            }
                            // Cache is valid if server date is not newer than cached date
                            // If serverDate equals cached date (304 response), cache is valid
                            const valid = serverDate <= (meta.lastModified || 0);
                            if (valid) {
                                const tRead = (typeof performance !== 'undefined' ? performance.now() : Date.now());
                                const records = await idbModule.idbGetAll(db);
                                const readMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - tRead;
                                
                                // DEBUG: Log UTF-8 encoding check after reading from IndexedDB
                                if (Array.isArray(records) && records.length > 0) {
                                    const testFile = records.find(f => (f.n || f.name || '').toLowerCase().includes('zus') && (f.n || f.name || '').toLowerCase().includes('tzliche'));
                                    if (testFile) {
                                        const fileName = testFile.n || testFile.name || '';
                                        console.log('[FilesLoader] UTF-8 DEBUG - After idbGetAll (cache valid):', {
                                            fileName: fileName,
                                            includesA: fileName.includes('Ä'),
                                            includesAumlaut: fileName.includes('\u00C4'),
                                            charCodes: Array.from(fileName).slice(0, 20).map(c => c.charCodeAt(0)),
                                            bytes: Array.from(new TextEncoder().encode(fileName)).slice(0, 20)
                                        });
                                    }
                                }
                                
                                const processedFiles = processFiles
                                    ? fileProcessor.processFilesList(records, processingOptions)
                                    : records;
                                
                                // DEBUG: Log UTF-8 encoding check after processing
                                if (Array.isArray(processedFiles) && processedFiles.length > 0) {
                                    const testFile = processedFiles.find(f => (f.n || f.name || '').toLowerCase().includes('zus') && (f.n || f.name || '').toLowerCase().includes('tzliche'));
                                    if (testFile) {
                                        const fileName = testFile.n || testFile.name || '';
                                        console.log('[FilesLoader] UTF-8 DEBUG - After processing (cache valid):', {
                                            fileName: fileName,
                                            includesA: fileName.includes('Ä'),
                                            includesAumlaut: fileName.includes('\u00C4')
                                        });
                                    }
                                }
                                // Ensure date_min/date_max in meta; compute if missing
                                try {
                                    if (!meta.date_min || !meta.date_max) {
                                        const bounds = this.filterMetadataGenerator.computeDateBounds(processedFiles);
                                        await idbModule.idbSetMeta(db, { ...meta, date_min: bounds.min || null, date_max: bounds.max || null });
                                    }
                                } catch(_){ }
                                const loadTime = Date.now() - startTime;
                                
                                if(window.ComponentLoadingStatus){
                                    let caption = 'Files loaded from IndexedDB';
                                    if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                                      const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                                      caption = window.TranslationRegistry.translate('loader_stage_files_loaded_from_indexeddb', lang);
                                    }
                                    window.ComponentLoadingStatus.update('filesStore', 'ready', caption);
                                }
                                
                                return { 
                                    files: processedFiles, 
                                    source: 'idb', 
                                    timestamp: meta.lastModified || null, 
                                    loadTime, 
                                    stats: this.localStorageManager.getStats(),
                                    hasOtAccess: !!meta.hasOtAccess 
                                };
                            }
                        }
                    } finally {
                        idbModule.closeDatabase(db);
                    }
                }
            } catch (e) { 
                /* continue to server load */ 
            }
        }
        
        // Load from server
        try {
            const result = await fileLoader.loadWithCache({ url: url, onProgress, forceUpdate });
            
            // DEBUG: Check ALL files with 'zus' after loading from server
            if (Array.isArray(result.files) && result.files.length > 0) {
                const testFiles = result.files.filter(f => (f.n || f.name || '').toLowerCase().includes('zus') && (f.n || f.name || '').toLowerCase().includes('tzliche'));
                if (testFiles.length > 0) {
                    const corrupted = testFiles.filter(f => {
                        const fileName = f.n || f.name || '';
                        return !fileName.includes('Ä') && !fileName.includes('\u00C4');
                    });
                    if (corrupted.length > 0) {
                        console.log('[FilesLoader] UTF-8 DEBUG - After loadWithCache (server source: ' + result.source + ') - Found ' + corrupted.length + ' CORRUPTED files:');
                        corrupted.forEach((f, idx) => {
                            const fileName = f.n || f.name || '';
                            console.log(`  [${idx}] ${fileName.substring(0, 60)}...`);
                        });
                    }
                }
            }
            
            const processedFiles = processFiles 
                ? fileProcessor.processFilesList(result.files, processingOptions)
                : result.files;
            
            // DEBUG: Check ALL files with 'zus' after processing (server source)
            if (Array.isArray(processedFiles) && processedFiles.length > 0) {
                const testFiles = processedFiles.filter(f => (f.n || f.name || '').toLowerCase().includes('zus') && (f.n || f.name || '').toLowerCase().includes('tzliche'));
                if (testFiles.length > 0) {
                    const corrupted = testFiles.filter(f => {
                        const fileName = f.n || f.name || '';
                        return !fileName.includes('Ä') && !fileName.includes('\u00C4');
                    });
                    if (corrupted.length > 0) {
                        console.log('[FilesLoader] UTF-8 DEBUG - After processing (server source: ' + result.source + ') - Found ' + corrupted.length + ' CORRUPTED files:');
                        corrupted.forEach((f, idx) => {
                            const fileName = f.n || f.name || '';
                            console.log(`  [${idx}] ${fileName.substring(0, 60)}...`);
                        });
                    }
                }
            }
            
            this.localStorageManager.stats.lastUpdate = Date.now();
            const loadTime = Date.now() - startTime;
            
            // ✅ CRITICAL: Status update is handled by FileLoader.loadFromServer() in .done()/.fail()
            // Only update here if result.source is 'idb' (already handled above) or 'cache' (localStorage)
            // For server requests, FileLoader.loadFromServer() updates status after HTTP completes
            
            // Persist entire list into IDB when supported
            try {
                if (idbModule.isIdbSupported() && idbModule.isIdbEnabled()) {
                    const db = await idbModule.openDatabase();
                    try {
                        // DEBUG: Check ALL files with 'zus' before saving to IndexedDB
                        if (Array.isArray(processedFiles) && processedFiles.length > 0) {
                            const testFiles = processedFiles.filter(f => (f.n || f.name || '').toLowerCase().includes('zus') && (f.n || f.name || '').toLowerCase().includes('tzliche'));
                            if (testFiles.length > 0) {
                                const corrupted = testFiles.filter(f => {
                                    const fileName = f.n || f.name || '';
                                    return !fileName.includes('Ä') && !fileName.includes('\u00C4');
                                });
                                if (corrupted.length > 0) {
                                    console.log('[FilesLoader] UTF-8 DEBUG - Before saving to IndexedDB - Found ' + corrupted.length + ' CORRUPTED files:');
                                    corrupted.forEach((f, idx) => {
                                        const fileName = f.n || f.name || '';
                                        console.log(`  [${idx}] ${fileName.substring(0, 60)}...`);
                                    });
                                }
                            }
                        }
                        
                        const tWrite = (typeof performance !== 'undefined' ? performance.now() : Date.now());
                        try { if (window.mainApp && typeof window.mainApp.updateLoaderProgress==='function') { window.mainApp.updateLoaderProgress(null, 'Saving files…'); } } catch(_) {}
                        await idbModule.idbClearFiles(db);
                        await idbModule.idbBulkUpsert(db, processedFiles);
                        const writeMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - tWrite;
                        // Compute date bounds from processed files
                        let bounds = { min: null, max: null };
                        try { bounds = this.filterMetadataGenerator.computeDateBounds(processedFiles); } catch(_){ }
                        await idbModule.idbSetMeta(db, {
                            lastModified: result.timestamp || Date.now(),
                            count: processedFiles.length,
                            version: this.currentVersion,
                            builtAt: new Date().toISOString(),
                            date_min: bounds.min || null,
                            date_max: bounds.max || null,
                            hasOtAccess: !!result.hasOtAccess
                        });
                        
                        // Generate filter metadata
                        try {
                            const filterMeta = this.filterMetadataGenerator.generateFilterMetadata(processedFiles);
                            await this.filterMetadataGenerator.saveFilterMetadata(db, filterMeta);
                        } catch (filterErr) {
                            if (window.HyperLog) { window.HyperLog.warn('[FilesLoader] Filter metadata generation failed', { error: filterErr?.message }); }
                        }
                        
                        try { if (window.mainApp && typeof window.mainApp.updateLoaderProgress==='function') { window.mainApp.updateLoaderProgress(null, 'Done'); } } catch(_) {}
                    } finally {
                        idbModule.closeDatabase(db);
                    }
                }
            } catch (persistErr) {
                const debugMode = new URLSearchParams(window.location.search).get('debug');
                if (debugMode === '1') {
                    console.warn('[FilesLoader] IDB persist failed, continuing without IDB:', persistErr);
                }
            }

            return {
                files: processedFiles,
                source: 'server',
                timestamp: result.timestamp,
                loadTime: loadTime,
                stats: this.localStorageManager.getStats(),
                hasOtAccess: !!result.hasOtAccess
            };
            
        } catch (error) {
            const debugMode = new URLSearchParams(window.location.search).get('debug');
            if (debugMode === '1') {
                console.error('[FilesLoader] Failed to load files:', error);
            }
            
            throw error;
        }
    }

    /**
     * Check if summaries cache is valid (HEAD-only)
     * @param {string} url - Summaries API URL
     * @param {Function} lastModified - Function to get last modified date
     * @param {Object} idbModule - IDB module with functions
     * @returns {Promise<boolean>} - Cache validity status
     */
    async isSummariesValid(url = '/api/files/summaries', lastModified, idbModule) {
        try {
            if (idbModule.isIdbSupported() && idbModule.isIdbEnabled()) {
                const db = await idbModule.openDatabase();
                try {
                        const meta = await idbModule.idbGetSummariesMeta(db);
                        const serverDate = lastModified(url, meta ? meta.lastModified : null);
                        return !!meta && serverDate !== null && !isNaN(serverDate) && serverDate <= (meta.lastModified || 0);
                } finally { idbModule.closeDatabase(db); }
            }
        } catch(_) { /* noop */ }
        return false;
    }

    /**
     * Load summaries dataset with caching by Last-Modified, store meta in IDB.
     * Returns { summaries, source, timestamp, metaOnly? }
     * @param {Object} options - Loading options
     * @param {Function} lastModified - Function to get last modified date
     * @param {Object} idbModule - IDB module with functions
     * @param {Object} fileLoader - File loader module
     * @returns {Promise<Object>} - Result with summaries and metadata
     */
    async loadSummaries(options = {}, lastModified, idbModule, fileLoader) {
        const { url = '/api/files/summaries', forceUpdate = false } = options;

        // For summaries with versioned URL, rely entirely on browser cache
        // Version in URL ensures freshness - if version changes, browser will fetch new data
        // Don't use IDB cache for versioned URLs - let browser handle caching
        // This ensures that when summary.json changes and manifest is updated, new version is fetched

        // Fetch from server
        if (typeof window.mainApp?.updateLoaderProgress === 'function') { 
            try { 
                window.mainApp.updateLoaderProgress(70, (typeof translate==='function'? translate('loader_stage_download_summaries'): 'Summaries…')); 
            } catch(_) {} 
        }
        const res = await fileLoader.loadSummaries({ url });
        const serverTimestamp = res.serverTimestamp || Date.now();
        const summariesObj = res.summaries || {};

        // FULL INGEST into IDB (primary load requirement)
        try {
            if (idbModule.isIdbSupported() && idbModule.isIdbEnabled()) {
                const entries = Object.entries(summariesObj);
                const total = entries.length;
                const db = await idbModule.openDatabase();
                try {
                    // If meta indicates different count or force update - clear
                    const metaPrev = await idbModule.idbGetSummariesMeta(db);
                    const prevCount = await idbModule.idbGetSummariesCount(db);
                    if (metaPrev && typeof metaPrev.count === 'number') {
                        if (metaPrev.count !== total || (metaPrev.lastModified || 0) !== serverTimestamp) {
                            await idbModule.idbClearSummaries(db);
                        }
                    } else if (prevCount > 0) {
                        await idbModule.idbClearSummaries(db);
                    }

                    // Write in chunks
                    const chunkSize = 2000;
                    for (let i = 0; i < total; i += chunkSize) {
                        const chunk = entries.slice(i, i + chunkSize).map(([key, value]) => ({ key, value }));
                        await idbModule.idbBulkUpsertSummaries(db, chunk);
                        const processed = Math.min(total, i + chunk.length);
                        const percent = Math.round((processed / Math.max(1, total)) * 100);
                        try { 
                            if (window.mainApp && typeof window.mainApp.updateLoaderProgress === 'function') { 
                                window.mainApp.updateLoaderProgress(percent, (typeof translate==='function'? translate('loader_stage_download_summaries'): 'Summaries…')); 
                            } 
                        } catch(_) {}
                    }
                    await idbModule.idbSetSummariesMeta(db, { lastModified: serverTimestamp, count: total });
                } finally { idbModule.closeDatabase(db); }
            }
        } catch (e) {
            // Continue even if IDB unavailable
        }

        return { summaries: null, source: 'server', timestamp: serverTimestamp, ingested: true };
    }

    /**
     * Get summaries by keys, persisting new ones to IDB if provided in summariesSource.
     * @param {Array} keys - Keys to fetch
     * @param {Object} summariesSource - Source object with summaries
     * @param {Object} idbModule - IDB module with functions
     * @returns {Promise<Array>} - Summaries array
     */
    async getSummariesByKeys(keys = [], summariesSource = null, idbModule) {
        if (!Array.isArray(keys) || keys.length === 0) return [];
        let results = new Array(keys.length).fill(undefined);
        try {
            if (idbModule.isIdbSupported() && idbModule.isIdbEnabled()) {
                const db = await idbModule.openDatabase();
                try {
                    const existing = await idbModule.idbGetSummariesByKeys(db, keys);
                    results = existing.slice();
                    const missing = [];
                    for (let i = 0; i < keys.length; i++) {
                        if (typeof results[i] === 'undefined') missing.push({ idx: i, key: keys[i] });
                    }
                    if (missing.length > 0 && summariesSource && summariesSource.summaries) {
                        const items = [];
                        missing.forEach(({ idx, key }) => {
                            if (Object.prototype.hasOwnProperty.call(summariesSource.summaries, key)) {
                                const val = summariesSource.summaries[key];
                                results[idx] = val;
                                items.push({ key, value: val });
                            }
                        });
                        if (items.length > 0) {
                            await idbModule.idbBulkUpsertSummaries(db, items);
                        }
                    }
                } finally { idbModule.closeDatabase(db); }
            }
        } catch(_) { /* noop */ }
        return results;
    }
}

export { FilesLoader };

